import * as React from "react";
import { Keyboard, Platform, TouchableWithoutFeedback, View, ScrollView } from "react-native";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
import "react-native-get-random-values";

import { Business, Organization, User, Image as DCImage } from "../../types";

import {
  Box,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography,
  Button,
} from "@mui/material";

import { useForm } from "react-hook-form";
import BusinessImagePicker from "../imagePicker";

import { collectAllAnswersSnapshot, hydrateStepAnswersFor } from "../../lib/investigationTool/investigationToolLocal";
import { uploadStepAttachments, deleteAttachment, saveInvestigationToFirestore } from "../../Firebase/queries/businessInvestigation";

import { onSnapshot, collection, query, orderBy, where } from "firebase/firestore";
import { db, storage } from "../../Firebase/firebase";
import useAppNavigation from "../../hooks/useAppNavigation";
import { useAppDispatch } from "../../redux/hooks";
import { notifyAndLogError } from "../../redux/snackbarSlice";

import { BASE_STEPS, OVERLAYS_BY_CATEGORY, StepConfig } from "../../lib/investigationTool/stepQuestions";
import { StepBoxes, createSetAnswer, AnswersState } from "../../lib/investigationTool/stepBoxes";

type AttachmentView = {
  uuid: string;
  fileName?: string;
  url?: string;
  downloadUrl?: string;
  label?: string;
  step?: number;
};

type BusinessEditProps = {
  business: Business;
  organization: Organization;
  user: User;
};

const BusinessInvestigationTool = ({ business, organization, user }: BusinessEditProps) => {
  const [activeStep, setActiveStep] = React.useState(0);
  const [answers, setAnswers] = React.useState<AnswersState>({});
  const [attachments, setAttachments] = React.useState<AttachmentView[]>([]);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const orgId = organization.id;
  const busId = business.id;
  const navigation = useAppNavigation();
  const dispatch = useAppDispatch();

  // Image picker
  const { control, getValues, reset, formState: { isValid }, watch } = useForm<{ images: DCImage[] }>({
    defaultValues: { images: [] },
    mode: "onChange",
  });

  // Watch images to determine if Next should be disabled
  const pendingImages = watch("images");
  const showDescriptionError = pendingImages?.length > 0 && !isValid;

  React.useEffect(() => {
    if (!orgId || !busId) return;

    // Clear attachments immediately when step changes to prevent stale data display
    setAttachments([]);

    const q = query(
      collection(db, `organizations/${orgId}/businesses/${busId}/attachments`),
      where("step", "==", activeStep),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(q, (snap) => {
      const list: AttachmentView[] = [];
      snap.forEach((docSnap) => list.push(docSnap.data() as AttachmentView));
      setAttachments(list);
    });

    return () => unsub();
  }, [orgId, busId, activeStep]);

  // Step description text
  const StepBody: React.FC<{ step: number }> = ({ step }) => {
    if (step === 0) return <Typography>The purpose of this step is to ensure that the business is verified by a Google search:</Typography>;
    if (step === 1) return <Typography>The purpose of this step is to ensure that the business is verified by an active website:</Typography>;
    if (step === 2) return <Typography>The purpose of this step is to ensure that the business is verified by social media accounts:</Typography>;
    if (step === 3) return <Typography>The purpose of this step is to ensure that the business is verified via mobile outreach:</Typography>;
    return null;
  };

  // Merge BASE_STEPS + overlays for the current category
  const stepsConfig: StepConfig[] = React.useMemo(() => {
    const overlays = OVERLAYS_BY_CATEGORY[String(business.category)] || {};
    return BASE_STEPS.map((base, idx) => ({
      label: base.label,
      questions: [...base.questions, ...(overlays[idx] ?? [])],
    }));
  }, [business.category]);

  const totalSteps = stepsConfig.length;

  // Hydrate answers from localStorage for the active step
  React.useEffect(() => {
    const scopeKey = String(business.category ?? "unknown");
    const stepCfg = stepsConfig[activeStep];
    if (!stepCfg) return;

    const businessKey = (business as any).id || (business as any).name;
    const loaded = hydrateStepAnswersFor(businessKey, scopeKey, activeStep, stepCfg);
    setAnswers((prev) => ({ ...prev, [activeStep]: loaded }));
  }, [activeStep, stepsConfig, business.id, business.name, business.category]);

  const setAnswer = React.useMemo(
    () =>
      createSetAnswer({
        businessIdOrName: (business as any).id || (business as any).name,
        scopeKey: String(business.category ?? "unknown"),
        setAnswers,
      }),
    [business.id, business.name, business.category, setAnswers]
  );

  // Upload any selected images for this step
  const pushAttachments = React.useCallback(
    async (images: DCImage[], step: number) => {
      if (!images?.length) return;

      try {
        await uploadStepAttachments({
          db,
          storage,
          orgId,
          busId,
          step,
          images: images.map((i) => ({
            imageUrl: (i as any).imageUrl,
            imageDescription: (i as any).imageDescription,
            fileName: (i as any).fileName,
            contentType: (i as any).contentType,
          })),
        });
      } catch (err) {
        console.error("Failed to upload attachments", err);

        dispatch(
          notifyAndLogError(
            "Failed to upload attachments. Please try again."
          )
        );
      }
    },
    [orgId, busId, dispatch]
  );

const handleDeleteAttachment = React.useCallback(
  async (att: { uuid: string; storageRefPath?: string }) => {
    const { uuid } = att;
    setIsDeleting(true);

    setAttachments((prev) => prev.filter((a) => a.uuid !== uuid));

    try {
      await deleteAttachment({
        db,
        storage,
        orgId,
        busId,
        uuid,
        storageRefPath: att.storageRefPath,
      });
    } catch (err) {
      console.error("Failed to delete attachment", err);

        // This function preemptively deletes the id from the local array
        // And if the firestore delete fails this catch, we then re-add the
        // Attachment back to the local collection
      setAttachments((prev) =>
        prev.some((a) => a.uuid === uuid) ? prev : [...prev, att as any]
      );

      dispatch(
        notifyAndLogError(
          "Failed to delete attachment. Please try again."
        )
      );
    } finally {
      setIsDeleting(false);
    }
  },
  [orgId, busId, dispatch]
);


  const handleNext = React.useCallback(
    async () => {
      const { images: pending } = getValues();
      if (pending?.length) {
        await pushAttachments(pending, activeStep);
        reset({ images: [] });
      }

      const isLast = activeStep >= totalSteps - 1;
        if (isLast) {
          const scopeKey = String(business.category ?? "unknown");
          const businessKey = (business as any).id || (business as any).name;

      const fullAnswers = collectAllAnswersSnapshot(businessKey, scopeKey, stepsConfig);

        try {
          await saveInvestigationToFirestore({
            db,
            orgId,
            busId,
            investigationId: "latest",
            business: {
              id: (business as any).id,
              name: (business as any).name,
              category: (business as any).category,
              status: (business as any).status,
            },
            user: {
              id: (user as any).id,
              email: (user as any).email,
              displayName: (user as any).displayName,
            },
            steps: stepsConfig.map((s) => ({ label: s.label })),
            answers: fullAnswers,
          });
        } catch (err) {
          console.error("Failed to save investigation", err);

          dispatch(
            notifyAndLogError(
              "Failed to save the investigation. Please try again."
            )
          );

          return;
        }

        navigation.goBack();
        return;
      }

      setActiveStep((prev) => Math.min(prev + 1, totalSteps - 1));
    },
    [
      activeStep,
      totalSteps,
      getValues,
      pushAttachments,
      reset,
      business,
      orgId,
      busId,
      stepsConfig,
      user,
      navigation,
      dispatch,
    ]
  );


  const handleBack = React.useCallback(() => {
    setActiveStep((prev) => Math.max(0, prev - 1));
  }, []);

  return (
    <KeyboardAwareScrollView contentContainerStyle={{ flex: 1 }}>
      <TouchableWithoutFeedback onPress={Keyboard.dismiss} disabled={Platform.OS === "web"}>
        <View>
          <Box sx={{ width: "100%" }}>
            <Paper elevation={3} sx={{ maxWidth: 720, mx: "auto", mt: 1, p: 2, borderRadius: 2 }}>
              <Typography variant="h4" sx={{ mb: 1 }}>
                {business.name} - Investigation Tool
              </Typography>

              <Stepper activeStep={activeStep}>
                {stepsConfig.map((s, i) => (
                  <Step key={s.label} completed={i < activeStep}>
                    <StepLabel>{s.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Box sx={{ mt: 2 }}>
                <Typography sx={{ mb: 1 }}>
                  Step {activeStep + 1}: {stepsConfig[activeStep]?.label}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <StepBody step={activeStep} />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <StepBoxes step={activeStep} stepsConfig={stepsConfig} answers={answers} onChange={setAnswer} />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Attachments
                  </Typography>

                  <BusinessImagePicker control={control} />

                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator
                    contentContainerStyle={{ display: "flex", flexDirection: "row", paddingVertical: 4 }}
                    style={{ width: "100%" }}
                  >
                    {attachments.map((att) => (
                      <Box
                        key={att.uuid}
                        sx={{
                          width: 200,
                          mr: 1,
                          border: "1px solid #eee",
                          borderRadius: 1.5,
                          p: 1,
                          overflow: "hidden",
                          position: "relative",
                          backgroundColor: "#fff",
                        }}
                      >
                        <Button
                          size="small"
                          disabled={isDeleting}
                          onClick={() => handleDeleteAttachment(att)}
                          sx={{ position: "absolute", top: 6, right: 6, minWidth: 0, p: 0.5, zIndex: 1 }}
                        >
                          ✕
                        </Button>

                        {att.downloadUrl ? (
                          <img
                            src={att.downloadUrl}
                            alt={att.fileName || att.uuid}
                            style={{ width: "100%", height: 130, objectFit: "cover", borderRadius: 6, display: "block" }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: "100%",
                              height: 130,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: 1,
                              bgcolor: "#fafafa",
                            }}
                          >
                            <Typography variant="caption">Processing…</Typography>
                          </Box>
                        )}

                        <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                          {att.label ?? att.fileName}
                        </Typography>
                      </Box>
                    ))}
                  </ScrollView>
                </Box>

                {showDescriptionError && (
                  <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                    Please add a description to all uploaded images before continuing.
                  </Typography>
                )}

                <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                  <Button color="inherit" disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
                    Back
                  </Button>
                  <Box sx={{ flex: "1 1 auto" }} />
                  <Button
                    onClick={handleNext}
                    disabled={showDescriptionError}
                  >
                    {activeStep === totalSteps - 1 ? "Finish" : "Next"}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Box>
        </View>
      </TouchableWithoutFeedback>
    </KeyboardAwareScrollView>
  );
};

export default BusinessInvestigationTool;
